// App.tsx
import { useEffect, useCallback, useRef, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { App as AntdApp, ConfigProvider, message } from 'antd';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { msalConfig } from './authConfig';

import FrontPage from '@pages/FrontPage';
import OatiPage from '@pages/Oati/Oati';
import NotFound from '@components/not-found/NotFound';
import Header from '@components/header/Header';
import IPPSignup from '@components/ipp-signup-section/IPPSignup';
import SessionWarningModal from '@components/session-warning-modal/SessionWarningModal';
import OatiRoute from '@components/routes/OatiRoute';
import MainAppRoute from '@components/routes/MainAppRoute';
import useSession from '@hooks/useSession';
import { useSessionTimeout } from '@hooks/useSessionTimeout';
import ApolloWrapper from '@graphql/client';
import { createTheme } from './theme';
import { REGISTERED_ROUTE_PATTERNS } from './routes';

import { BreadcrumbProvider } from './context/BreadcrumbContext';
import { appInsights } from './appInsights';
import { SelectedPeriodProvider } from './context/SelectedPeriodContext';
import styles from './App.module.css';


// Create and initialize MSAL instance ONCE
let msalInstancePromise: Promise<PublicClientApplication> | null = null;

function getMsalInstance(): Promise<PublicClientApplication> {
  msalInstancePromise ??= (async () => {
    const instance = new PublicClientApplication(msalConfig);
    await instance.initialize();

    // Set up event callbacks
    instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as any;
        const account = payload.account;
        instance.setActiveAccount(account);
      }
    });

    // Handle redirect promise
    await instance.handleRedirectPromise();

    return instance;
  })();

  return msalInstancePromise;
}


function AppContent() {
  const {
    isBusy,
    handleLogin,
    handleLogout,
    isLoggedIn,
    isAdmin,
    isSuperAdmin,
    isManager,
    isUser,
    isOattRateOwner,
    isOattBillPreparer,
    isOattBillApprover,
    loading,
    account,
    accessToken
  } = useSession();
  const isOattUser = isOattRateOwner || isOattBillPreparer || isOattBillApprover;

  const [apolloReady, setApolloReady] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirected = useRef(false);

  // Compute role-only flags (no overlap with higher-privilege roles)
  const isReporterOnly = isManager && !isUser && !isAdmin && !isSuperAdmin;
  // Approver who is not also a Preparer — cannot access /file-upload-logs
  const isApproverOnly = isAdmin && !isUser && !isSuperAdmin;

  // Redirect users after role detection
  useEffect(() => {
    // Guard: account must be resolved before redirecting.
    // Without this, loading can be false while account is still null (the window between
    // popup login populating accounts[] and useAccount() resolving), causing the redirect
    // to fire with all roles still false and land every user on /file-upload-logs.
    if (!loading && isLoggedIn && !!account && !hasRedirected.current) {
      if (isOattUser) {
        hasRedirected.current = true;
        if (location.pathname === '/' || location.pathname === '/login' || !location.pathname.startsWith('/oati')) {
          navigate('/oati', { replace: true });
        }
      } else if (location.pathname === '/' || location.pathname === '/login') {
        hasRedirected.current = true;
        // Route each role to their appropriate landing page
        let defaultRoute = '/file-upload-logs';        // Preparer / SuperAdmin
        if (isReporterOnly) defaultRoute = '/spp-reports-invoice-amounts';
        else if (isApproverOnly) defaultRoute = '/prod-stat-dashboard';
        navigate(defaultRoute, { replace: true });
      }
    }

    if (!isLoggedIn) {
      hasRedirected.current = false;
    }
  }, [loading, isLoggedIn, account, isOattUser, isReporterOnly, isApproverOnly, navigate, location.pathname]);

  // Apollo client initialization for non-OATI users
  useEffect(() => {
    if (accessToken && !isOattUser && !loading && isLoggedIn) {
      const timer = setTimeout(() => setApolloReady(true), 200);
      return () => clearTimeout(timer);
    } else {
      setApolloReady(false);
    }
  }, [accessToken, isOattUser, loading, isLoggedIn]);

  // Session timeout popup
  const { showWarning, timeLeft, extendSession, handleTimeout } = useSessionTimeout({
    isAuthenticated: isLoggedIn,
    onTimeout: () => {
      message.warning('Your session has expired due to inactivity.');
      handleLogout();
    },
    onWarning: () => {
      message.info('Your session is about to expire.');
    },
  });

  const handleLogoutClick = useCallback(() => {
    message.info('You have been logged out successfully.');
    handleLogout();
  }, [handleLogout]);

  const isKnownPath =
    location.pathname === '/' ||
    location.pathname === '/unauthorized' ||
    REGISTERED_ROUTE_PATTERNS.some((p) => p.test(location.pathname));

  // Unknown path — render immediately before any auth/loading logic
  if (!isKnownPath) {
    return <NotFound />;
  }

  // Loading during MSAL processes
  if (isBusy) {
    return (
      <>
        <Header showMenu={false} account={null} />
        <main className={styles.loadingContainer}>
          <h2 className={styles.loadingTitle}>Signing you in…</h2>
          <p>Please wait a moment.</p>
        </main>
      </>
    );
  }

  // Not logged in
  if (!isLoggedIn) {
    return <IPPSignup onLogin={handleLogin} />;
  }

  // Waiting for user roles
  if (loading) {
    return (
      <>
        <Header showMenu={false} account={account} />
        <main className={styles.centeredLoading}>
          <div className={styles.centeredContent}>
            <h2>Loading your profile...</h2>
            <p>Determining access level...</p>
          </div>
        </main>
      </>
    );
  }

  // Waiting for access token
  if (!accessToken) {
    return (
      <>
        <Header showMenu={false} account={account} />
        <main className={styles.loadingContainer}>
          <h2 className={styles.loadingTitle}>Loading your session...</h2>
          <p>Getting your access token...</p>
        </main>
      </>
    );
  }

  // OATT User isolated environment
  if (isOattUser) {
    return (
      <ApolloWrapper>
        <div>
          <SessionWarningModal
            open={showWarning}
            timeLeft={timeLeft}
            onExtend={extendSession}
            onLogout={handleTimeout}
          />

          <Routes>
            <Route
              path="/oati/*"
              element={
                <OatiRoute>
                  <OatiPage />
                </OatiRoute>
              }
            />
            <Route path="/unauthorized" element={<NotFound />} />
            <Route path="*" element={<Navigate to="/oati" replace />} />
          </Routes>
        </div>
      </ApolloWrapper>
    );
  }

  // Admin/User full application
  if (!apolloReady) {
    return (
      <>
        <Header showMenu={false} account={account} />
        <main className={styles.loadingContainer}>
          <h2 className={styles.loadingTitle}>Loading application...</h2>
          <p>Initializing...</p>
        </main>
      </>
    );
  }

  return (
    <Routes>
      <Route path="/unauthorized" element={<NotFound />} />
      <Route
        path="/*"
        element={
          <div>
            <Header showMenu account={account} onLogout={handleLogoutClick} />

            <SessionWarningModal
              open={showWarning}
              timeLeft={timeLeft}
              onExtend={extendSession}
              onLogout={handleTimeout}
            />

            <ApolloWrapper>
              <Routes>
                <Route
                  path="/*"
                  element={
                    <MainAppRoute>
                      <FrontPage />
                    </MainAppRoute>
                  }
                />
                <Route path="/oati" element={<Navigate to="/unauthorized" replace />} />
              </Routes>
            </ApolloWrapper>
          </div>
        }
      />
    </Routes>
  );
}

export default function App() {
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
  const [theme, setTheme] = useState(() => createTheme());

  // Initialize MSAL before rendering
  useEffect(() => {
    getMsalInstance().then(setMsalInstance).catch((error) => {
      appInsights?.trackException({ exception: error });
    });
  }, []);

  useEffect(() => {
    setTheme(createTheme());

    const observer = new MutationObserver(() => {
      setTheme(createTheme());
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style'],
    });

    const styleSheetObserver = new MutationObserver(() => {
      setTimeout(() => setTheme(createTheme()), 50);
    });

    styleSheetObserver.observe(document.head, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      styleSheetObserver.disconnect();
    };
  }, []);

  // Show loading until MSAL is initialized
  if (!msalInstance) {
    return (
      <ConfigProvider theme={theme}>
        <div className={styles.loadingContainer}>
          <h2 className={styles.loadingTitle}>Initializing authentication...</h2>
          <p>Please wait a moment.</p>
        </div>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={theme}>
      <AntdApp>
        <MsalProvider instance={msalInstance}>
          <SelectedPeriodProvider>
            <BreadcrumbProvider>
              <AppContent />
            </BreadcrumbProvider>
          </SelectedPeriodProvider>
        </MsalProvider>
      </AntdApp>
    </ConfigProvider>
  );
}